"use client";

import { useState, useEffect, useCallback } from "react";
import {
  ontologyListForeignConnections,
  ontologyCreateForeignConnection,
  ontologyGetForeignConnection,
  ontologyUpdateForeignConnection,
  ontologyDeleteForeignConnection,
  ontologyTestForeignConnection,
} from "../sdk/sdk.gen";
import type {
  CreateForeignConnectionRequest,
  UpdateForeignConnectionRequest,
  TestForeignConnectionRequest,
  ForeignConnectionWithDatasource,
  TestForeignConnectionResponse,
} from "../sdk/types.gen";

function unwrapResponse<T>(
  response: unknown,
  errorContext: string,
): T {
  const r = response as { data?: T; error?: unknown };
  if (r.error) {
    const message =
      typeof r.error === "string"
        ? r.error
        : ((r.error as { message?: string })?.message ??
          `Failed to ${errorContext}`);
    throw new Error(message);
  }
  if (r.data === undefined || r.data === null) {
    throw new Error(`Failed to ${errorContext}: empty response`);
  }
  return r.data;
}

export interface ForeignConnection {
  id: string;
  datasource_id: string;
  connection_name: string;
  schema_mapping?: Record<string, any>;
  federation_enabled: boolean;
  org_id: string;
  created_at: string;
  updated_at: string;
  datasource?: any;
}

interface UseForeignConnectionsResult {
  foreignConnections: ForeignConnection[];
  total: number;
  loading: boolean;
  error: string | null;
  createForeignConnection: (
    request: CreateForeignConnectionRequest
  ) => Promise<ForeignConnection>;
  updateForeignConnection: (
    connectionId: string,
    request: UpdateForeignConnectionRequest
  ) => Promise<ForeignConnection>;
  deleteForeignConnection: (connectionId: string) => Promise<void>;
  testForeignConnection: (
    request: TestForeignConnectionRequest
  ) => Promise<TestForeignConnectionResponse>;
  refetch: () => Promise<void>;
}

/**
 * Hook for managing foreign database connections
 * Enables direct querying of external PostgreSQL databases using foreign.* namespace
 *
 * @param orgId - Organization ID for fetching foreign connections
 * @param federationEnabledOnly - Only fetch federation-enabled connections (default: true)
 * @returns Foreign connections and operations
 */
export function useForeignConnections(
  orgId?: string,
  federationEnabledOnly: boolean = true
): UseForeignConnectionsResult {
  const [foreignConnections, setForeignConnections] = useState<
    ForeignConnection[]
  >([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchForeignConnections = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!orgId) {
        setForeignConnections([]);
        setTotal(0);
        return;
      }

      const response = await ontologyListForeignConnections({
        query: {
          org_id: orgId,
          federation_enabled_only: federationEnabledOnly,
        },
      });

      const connections = response.data?.connections || [];
      const totalCount = response.data?.total || 0;

      setForeignConnections(connections);
      setTotal(totalCount);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch foreign connections"
      );
      console.error("Error fetching foreign connections:", err);
    } finally {
      setLoading(false);
    }
  }, [orgId, federationEnabledOnly]);

  const createForeignConnection = useCallback(
    async (request: CreateForeignConnectionRequest) => {
      try {
        setError(null);

        console.log("🔵 Creating foreign connection:", {
          request,
          orgId,
          timestamp: new Date().toISOString(),
        });

        if (!orgId) {
          throw new Error(
            "Organization ID is required to create a foreign connection"
          );
        }

        const response = await ontologyCreateForeignConnection({
          body: request,
          query: { org_id: orgId },
        });

        const data = unwrapResponse<ForeignConnection>(
          response,
          "create foreign connection",
        );

        console.log("🟢 Foreign connection created successfully:", data);

        // Refresh the connections list after creating a new one
        await fetchForeignConnections();

        return data;
      } catch (err) {
        console.error("🔴 Error creating foreign connection:", {
          error: err,
          message: err instanceof Error ? err.message : "Unknown error",
          request,
          orgId,
        });

        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to create foreign connection";
        setError(errorMessage);
        throw err;
      }
    },
    [fetchForeignConnections, orgId]
  );

  const updateForeignConnection = useCallback(
    async (
      connectionId: string,
      request: UpdateForeignConnectionRequest
    ) => {
      try {
        setError(null);

        console.log("🔵 Updating foreign connection:", {
          connectionId,
          request,
          orgId,
          timestamp: new Date().toISOString(),
        });

        if (!orgId) {
          throw new Error(
            "Organization ID is required to update a foreign connection"
          );
        }

        const response = await ontologyUpdateForeignConnection({
          path: { connection_id: connectionId },
          body: request,
          query: { org_id: orgId },
        });

        const data = unwrapResponse<ForeignConnection>(
          response,
          "update foreign connection",
        );

        console.log("🟢 Foreign connection updated successfully:", data);

        // Refresh the connections list after updating
        await fetchForeignConnections();

        return data;
      } catch (err) {
        console.error("🔴 Error updating foreign connection:", {
          error: err,
          message: err instanceof Error ? err.message : "Unknown error",
          connectionId,
          request,
          orgId,
        });

        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to update foreign connection";
        setError(errorMessage);
        throw err;
      }
    },
    [fetchForeignConnections, orgId]
  );

  const deleteForeignConnection = useCallback(
    async (connectionId: string) => {
      try {
        setError(null);

        console.log("🔵 Deleting foreign connection:", {
          connectionId,
          orgId,
          timestamp: new Date().toISOString(),
        });

        if (!orgId) {
          throw new Error(
            "Organization ID is required to delete a foreign connection"
          );
        }

        await ontologyDeleteForeignConnection({
          path: { connection_id: connectionId },
          query: { org_id: orgId },
        });

        console.log("🟢 Foreign connection deleted successfully");

        // Refresh the connections list after deleting
        await fetchForeignConnections();
      } catch (err) {
        console.error("🔴 Error deleting foreign connection:", {
          error: err,
          message: err instanceof Error ? err.message : "Unknown error",
          connectionId,
          orgId,
        });

        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to delete foreign connection";
        setError(errorMessage);
        throw err;
      }
    },
    [fetchForeignConnections, orgId]
  );

  const testForeignConnection = useCallback(
    async (request: TestForeignConnectionRequest) => {
      try {
        setError(null);

        console.log("🔵 Testing foreign connection:", {
          request,
          orgId,
          timestamp: new Date().toISOString(),
        });

        if (!orgId) {
          throw new Error(
            "Organization ID is required to test a foreign connection"
          );
        }

        const response = await ontologyTestForeignConnection({
          body: request,
          query: { org_id: orgId },
        });

        const data = unwrapResponse<TestForeignConnectionResponse>(
          response,
          "test foreign connection",
        );

        console.log("🟢 Foreign connection test result:", data);

        return data;
      } catch (err) {
        console.error("🔴 Error testing foreign connection:", {
          error: err,
          message: err instanceof Error ? err.message : "Unknown error",
          request,
          orgId,
        });

        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to test foreign connection";
        setError(errorMessage);
        throw err;
      }
    },
    [orgId]
  );

  const refetch = useCallback(async () => {
    await fetchForeignConnections();
  }, [fetchForeignConnections]);

  useEffect(() => {
    fetchForeignConnections();
  }, [fetchForeignConnections]);

  return {
    foreignConnections,
    total,
    loading,
    error,
    createForeignConnection,
    updateForeignConnection,
    deleteForeignConnection,
    testForeignConnection,
    refetch,
  };
}

/**
 * Hook for getting a single foreign connection by name
 *
 * @param orgId - Organization ID
 * @param connectionName - Connection name to fetch
 * @returns Single foreign connection and operations
 */
export function useForeignConnection(
  orgId?: string,
  connectionName?: string | null
) {
  const [foreignConnection, setForeignConnection] =
    useState<ForeignConnectionWithDatasource | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchForeignConnection = useCallback(async () => {
    if (!orgId || !connectionName) {
      setForeignConnection(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await ontologyGetForeignConnection({
        path: { connection_name: connectionName },
        query: { org_id: orgId },
      });

      setForeignConnection(response.data ?? null);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch foreign connection"
      );
      console.error("Error fetching foreign connection:", err);
    } finally {
      setLoading(false);
    }
  }, [orgId, connectionName]);

  const refetch = useCallback(async () => {
    await fetchForeignConnection();
  }, [fetchForeignConnection]);

  useEffect(() => {
    if (orgId && connectionName) {
      fetchForeignConnection();
    }
  }, [fetchForeignConnection, orgId, connectionName]);

  return {
    foreignConnection,
    loading,
    error,
    refetch,
  };
}
